import * as React from "react";
import { AccessOverviewEditChartColorsElementEnum } from "@app/access-reports/admin";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { cloneDeep } from "lodash";
import { ColorResult, SketchPicker } from "react-color";
import { FlyoutFooter } from "@app/shared/Flyouts";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons"; 
import TooltipWrapper from "@app/shared/TooltipWrapper";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { QESTextInput } from "@app/shared/qes-standards/forms/TextInput";
import { AccessReportsWizardElement } from "@app/access-reports/shared/accessReportsWizardElement";
import { AccessReportsWizardElementTypeEnum } from "@app/access-reports/shared/accessReportsWizardElementTypeEnum";
import { ByProductDefault } from "@app/access-reports/shared/byProductDefault";

interface AccessOverviewEditChartColorsProps {
  byProductDefault: ByProductDefault;
  disableButtons: boolean;
  elements: AccessReportsWizardElement[];
  hideSidePanel: () => void;
  onSaveAccessOverviewChartColorsClick: (byProductDefault: ByProductDefault) => void;
}

const AccessOverviewEditChartColors: React.FC<AccessOverviewEditChartColorsProps> = (props: AccessOverviewEditChartColorsProps) => {
  const [clonedByProductDefault, setClonedByProductDefault] = React.useState<ByProductDefault>(null);
  const [displayColorPicker, setDisplayColorPicker] = React.useState<AccessOverviewEditChartColorsElementEnum>(null);

  React.useEffect(() => {
    extractChartColorDefaults();
  }, [props.byProductDefault]);

  const buildAccessOverviewElement = (elementId: AccessOverviewEditChartColorsElementEnum, required = false, errorText?: string): React.ReactNode => {
    const element: AccessReportsWizardElement = props.elements.find(x => x.elementId === elementId);
    const value: unknown = getValue(elementId);

    if (!!element) {
      switch (element.elementType) {
        case AccessReportsWizardElementTypeEnum.Checkbox:
          return (
            <div className="col-12">
              <QESCheckbox
                checked={value as boolean}
                disabled={props.disableButtons}
                label={element.title}
                testId={`access-reports-admin__access-overview-chart-colors--${element.elementId}-checkbox`}
                onChange={() => onCheckboxToggle(element.elementId as AccessOverviewEditChartColorsElementEnum)}
              />
            </div>
          );
        case AccessReportsWizardElementTypeEnum.ColorPicker:
          const presetColors: string[] = element.options.map(x => x.color as string);

          return (
            <React.Fragment>
              {!!element.title && (
                <div className="col-md-6">
                  <label className="label-s mb-0">
                    {element.title}
                    {required && (
                      <span className="text-danger">*</span>
                    )}
                  </label>
                </div>
              )}

              <div className="col-md-6 d-flex align-items-center">
                <div className="color-picker-swatch" onClick={() => onColorPickerClick(element.elementId as AccessOverviewEditChartColorsElementEnum)}>
                  <div className="color-picker-color" style={{ backgroundColor: value as string || "#000" }} />
                </div>

                {(displayColorPicker === element.elementId) &&
                  <div className="color-picker-popover">
                    <div
                      className="color-picker-cover"
                      onClick={() => onColorPickerClose()}
                    />

                    <SketchPicker
                      color={value as string || "#000"}
                      disableAlpha={true}
                      presetColors={presetColors}
                      onChange={onColorPickerChange}
                    />
                  </div>
                }
              </div>
            </React.Fragment>
          );
        case AccessReportsWizardElementTypeEnum.Dropdown:
          return (
            <React.Fragment>
              {!!element.title && (
                <div className="col-md-6">
                  <label className="label-s mb-0">
                    {element.title}
                    {required && (
                      <span className="text-danger">*</span>
                    )}
                  </label>
                </div>
              )}

              <div className="col-md-6">
                <QESSelect
                  allowEmpty={true}
                  disabled={props.disableButtons}
                  testId={`access-reports-admin__access-overview-chart-colors--${element.elementId}-dropdown`}
                  label=""
                  onChange={(e) => onSelectChange(element.elementId as AccessOverviewEditChartColorsElementEnum, e)}
                  value={value.toString() || ""}
                >
                  {!!element.options && element.options.map((option, optionIndex) => (
                    <option
                      key={optionIndex}
                      value={option.id}
                    >
                      {option.displayName ? option.displayName : option.name}
                    </option>
                  ))}
                </QESSelect>
              </div>
            </React.Fragment>
          );
        case AccessReportsWizardElementTypeEnum.Input:
          return (
            <React.Fragment>
              {!!element.title && (
                <div className="col-md-6">
                  <label className="label-s mb-0">
                    {element.title}
                    {required && (
                      <span className="text-danger">*</span>
                    )}
                  </label>
                </div>
              )}

              <div className="col-md-6">
                <QESTextInput
                  testId={`access-reports-admin__access-overview-chart-colors--${element.elementId}-input`}
                  disabled={props.disableButtons}
                  errorText={errorText}
                  isValid={getInputValidity(element.elementId as AccessOverviewEditChartColorsElementEnum, value?.toString())}
                  label=""
                  onChange={(e) => onInputChange(element.elementId as AccessOverviewEditChartColorsElementEnum, e)}
                  unrequiredValidation={required}
                  value={value.toString() || ""}
                />
              </div>
            </React.Fragment>
          );
        default:
          break;
      }
    }

    return (
      <div className="form-group mb-3">
        <QesIcon iconName={QesIcons.AlertHexagon} description="Error" className="text-danger me-1 mb3" />
        <span className="text-muted">
          Error
        </span>
      </div>
    );
  }

  const buildDisabledSaveButtonTooltipText = (): string => {
    let tooltipText = "";

    if (props.disableButtons) {
      return "In order to save access overview chart colors, please check-out the template";
    }

    // only assess whether the hidden elements, specific to editing chart colors, have default values in place
    const elements: AccessReportsWizardElement[] = props.elements.filter(x => x.hidden);

    for (const element of elements) {
      if (element.defaultValue === null || element.defaultValue === undefined || element.defaultValue === "") {
        if (!tooltipText) {
          tooltipText = tooltipText + "Please provide a value for the following elements:";
          tooltipText = tooltipText + " " + splitKey(element.elementId);
        } else {
          tooltipText = tooltipText + ", " + splitKey(element.elementId);
        }
      }
    }

    return tooltipText;
  }

  const extractChartColorDefaults = (): void => {
    if (!!props.byProductDefault) {
      const newClonedByProductDefault: ByProductDefault = cloneDeep(props.byProductDefault);

      setClonedByProductDefault(newClonedByProductDefault);
    }
  }

  const getInputValidity = (elementId: AccessOverviewEditChartColorsElementEnum, value: string): boolean => {
    if (elementId === AccessOverviewEditChartColorsElementEnum.GraphMiles) {
      if (!value.match(/^[0-9]*$/) || parseInt(value) > 99 || parseInt(value) < 1) {
        return false;
      }
    }

    return true;
  }

  const getValue = (elementId: AccessOverviewEditChartColorsElementEnum): unknown => {
    if (!!clonedByProductDefault) {
      return clonedByProductDefault[elementId];
    }

    return "";
  }

  const onCheckboxToggle = (elementId: AccessOverviewEditChartColorsElementEnum): void => {
    const newClonedByProductDefault: ByProductDefault = { ...clonedByProductDefault };
    newClonedByProductDefault[elementId] = !newClonedByProductDefault[elementId] as boolean;

    setClonedByProductDefault(newClonedByProductDefault);
  }

  const onInputChange = (elementId: AccessOverviewEditChartColorsElementEnum, event: React.ChangeEvent<HTMLInputElement>): void => {
    const newClonedByProductDefault: ByProductDefault = { ...clonedByProductDefault };
    const value: string = event.target.value;

    if (elementId === AccessOverviewEditChartColorsElementEnum.GraphMiles) {
      // parsing the value for comparison and to remove leading zeros
      const parsedValue = parseInt(value);

      if (isNaN(parsedValue) || parsedValue === 0) {
        // if the graph miles input is not a number or 0, display the min value, 1
        newClonedByProductDefault[elementId] = 1;
      } else {
        if (value.match(/^[0-9]*$/) && parsedValue <= 99 && parsedValue >= 1) {
          // only whole numbers between 1 and 99 are valid values
          newClonedByProductDefault[elementId] = parsedValue;
        }
      }
    } else {
      newClonedByProductDefault[elementId] = value;
    }

    setClonedByProductDefault(newClonedByProductDefault);
  }

  const onSelectChange = (elementId: AccessOverviewEditChartColorsElementEnum, event: React.ChangeEvent<HTMLSelectElement>): void => {
    const newClonedByProductDefault: ByProductDefault = { ...clonedByProductDefault };
    newClonedByProductDefault[elementId] = event.target.value;

    setClonedByProductDefault(newClonedByProductDefault);
  }

  const splitKey = (key: string): string => {
    const splitKey: string = key.replace(/([A-Z])/g, " $1");
    const formattedKey: string = (splitKey.charAt(0).toUpperCase() + splitKey.slice(1));

    return formattedKey;
  }

  const onColorPickerClick = (accessOverviewElementId: AccessOverviewEditChartColorsElementEnum): void => {
    if (accessOverviewElementId === displayColorPicker) {
      setDisplayColorPicker(null);
    } else {
      setDisplayColorPicker(accessOverviewElementId);
    }
  }

  const onColorPickerChange = (color: ColorResult): void => {
    const newClonedByProductDefault: ByProductDefault = { ...clonedByProductDefault };
    newClonedByProductDefault[displayColorPicker] = color.hex;

    setClonedByProductDefault(newClonedByProductDefault);
  }

  const onColorPickerClose = (): void => {
    setDisplayColorPicker(null);
  }

  const disabledSavedButtonTooltipText = buildDisabledSaveButtonTooltipText();

  return (
    <React.Fragment>
      <div className="row">
        <div className="col-12">
          <label className="label-m, mb-3">
            Access Filter Pie Chart Options
          </label>
        </div>
      </div>

      <div className="row align-items-center mb-3">
        {buildAccessOverviewElement(AccessOverviewEditChartColorsElementEnum.WithColor, true)}
      </div>

      <div className="row align-items-center mb-3">
        {buildAccessOverviewElement(AccessOverviewEditChartColorsElementEnum.WithoutColor, true)}
      </div>

      <div className="row align-items-center mb-5">
        {buildAccessOverviewElement(AccessOverviewEditChartColorsElementEnum.ExcludedColor, true)}
      </div>

      <div className="row">
        <div className="col-12">
          <label className="label-m mb-3">
            Distance Chart Options
          </label>
        </div>
        <div className="col-12">
          <label className="label-s mb-0">
            Providers
            <span className="text-danger">*</span>
          </label>
        </div>
      </div>

      <div className="row mb-3">
        {buildAccessOverviewElement(AccessOverviewEditChartColorsElementEnum.ProviderOne)}
        {buildAccessOverviewElement(AccessOverviewEditChartColorsElementEnum.ProviderOneColor)}
      </div>

      <div className="row mb-3">
        {buildAccessOverviewElement(AccessOverviewEditChartColorsElementEnum.ProviderTwo)}
        {buildAccessOverviewElement(AccessOverviewEditChartColorsElementEnum.ProviderTwoColor)}
      </div>

      <div className="row mb-3">
        {buildAccessOverviewElement(AccessOverviewEditChartColorsElementEnum.ProviderThree)}
        {buildAccessOverviewElement(AccessOverviewEditChartColorsElementEnum.ProviderThreeColor)}
      </div>

      <div className="row mb-3">
        {buildAccessOverviewElement(AccessOverviewEditChartColorsElementEnum.ProviderFour)}
        {buildAccessOverviewElement(AccessOverviewEditChartColorsElementEnum.ProviderFourColor)}
      </div>

      <div className="row mb-5">
        {buildAccessOverviewElement(AccessOverviewEditChartColorsElementEnum.ProviderFive)}
        {buildAccessOverviewElement(AccessOverviewEditChartColorsElementEnum.ProviderFiveColor)}
      </div>

      <div className="row align-items-center mb-3">
        {buildAccessOverviewElement(AccessOverviewEditChartColorsElementEnum.GraphMiles, true, "Please input a value between 1 and 99")}
      </div>

      <div className="row align-items-center mb-3">
        {buildAccessOverviewElement(AccessOverviewEditChartColorsElementEnum.ShowMinimums)}
      </div>

      <div className="row align-items-center mb-3">
        {buildAccessOverviewElement(AccessOverviewEditChartColorsElementEnum.ShowMaximums)}
      </div>

      <FlyoutFooter
        submitButton={
          <TooltipWrapper
            text={disabledSavedButtonTooltipText}
            position="top"
          >
            <QESButton
              disabled={props.disableButtons || !!disabledSavedButtonTooltipText}
              onClick={() => props.onSaveAccessOverviewChartColorsClick(clonedByProductDefault)}
              qesTestId="access-reports-admin__access-overview-chart-colors--save"
              qesType="primary"
            >
              Save
            </QESButton>
          </TooltipWrapper>
        }
      />
    </React.Fragment>
  );
}

export { AccessOverviewEditChartColors };
